import { useEffect, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CreateFeatureForm } from './CreateFeatureForm';
import { InternalLayer } from 'hooks/internalLayer/types';
import {
  CreateIcsIncidentLocationBody,
  IcsIncidentLocation,
} from 'hooks/icsLocation/icsIncidentLocation.types';

type LayerFeaturesEditorProps = {
  internalLayer?: InternalLayer | null;
  showAddObjectButton?: boolean;
  showSearch?: boolean;
  searchTerm?: string;
  onChangeSearchTerm?: (term: string) => void;
  onSubmitLocation: ({
    location,
    pendingGraphicId,
  }: {
    location: CreateIcsIncidentLocationBody;
    pendingGraphicId?: string;
  }) => Promise<void> | void;
  onCancelLocation?: () => void;
  onEditingChange?: (editing: boolean) => void;
  editingLocation?: IcsIncidentLocation | null;
  onCancelEdit?: () => void;
};

export function LayerFeaturesEditor({
  internalLayer,
  showAddObjectButton,
  showSearch = true,
  searchTerm,
  onChangeSearchTerm,
  onSubmitLocation,
  onCancelLocation,
  onEditingChange,
  editingLocation,
  onCancelEdit,
}: LayerFeaturesEditorProps) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const setFormOpen = (next: boolean) => {
    setIsFormOpen(next);
    onEditingChange?.(next);
  };

  useEffect(() => {
    if (editingLocation) setIsFormOpen(false);
  }, [editingLocation]);

  return (
    <div className="layer-objects-editor dark tw:text-foreground">
      <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2">
        {showSearch && (
          <div className="tw:relative tw:min-w-0 tw:flex-1">
            <Search className="tw:pointer-events-none tw:absolute tw:top-1/2 tw:left-2 tw:size-3.5 tw:-translate-y-1/2 tw:text-muted-foreground" />
            <Input
              value={searchTerm ?? ''}
              onChange={(event) => onChangeSearchTerm?.(event.target.value)}
              placeholder="Search objects..."
              aria-label="Search objects"
              className="tw:h-8 tw:ps-8 tw:text-xs"
            />
          </div>
        )}
        {showAddObjectButton && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="tw:ms-auto tw:shrink-0"
            onClick={() => {
              onCancelEdit?.();
              setFormOpen(!isFormOpen);
            }}
          >
            <Plus className="tw:size-4" />
            Add Object
          </Button>
        )}
      </div>

      {(isFormOpen || editingLocation) && (
        <CreateFeatureForm
          key={editingLocation?.id ?? 'new-object'}
          internalLayer={internalLayer}
          existingLocation={editingLocation}
          onSubmit={onSubmitLocation}
          onClose={() => {
            if (editingLocation) onCancelEdit?.();
            setFormOpen(false);
          }}
          onDiscard={onCancelLocation}
        />
      )}
    </div>
  );
}
